import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TopicView } from "./TopicView";

// Walk parent_category chain → array of slugs root → leaf
const getCategoryPath = (category: any): string[] => {
  const slugs: string[] = [];
  let c = category;
  while (c) {
    if (c.slug) slugs.unshift(c.slug);
    c = c.parent_category;
  }
  return slugs;
};

// Builds canonical hierarchical URL for any category by slug.
// Used to recover from stale single-slug links (e.g. /new-jersey-youth-soccer)
// that should redirect to /youth-soccer-forum-usa/new-jersey-youth-soccer.
const useCanonicalCategoryPath = (slug: string | undefined) => {
  return useQuery({
    queryKey: ["canonical-category-path", slug],
    enabled: !!slug,
    staleTime: 1000 * 60 * 60, // 1h — slugs rarely change
    queryFn: async () => {
      if (!slug) return null;
      const { data, error } = await supabase
        .from("categories")
        .select(
          `slug, level,
           parent_category:parent_category_id (
             slug, level,
             parent_category:parent_category_id ( slug, level )
           )`
        )
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const segments = getCategoryPath(data);
      return "/" + segments.join("/");
    },
  });
};
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  User,
  Clock,
  Pin,
  Plus,
  ChevronRight,
  Home,
  HelpCircle,
} from "lucide-react";
import { formatCategoryDisplayName } from "@/utils/categoryHelpers";

import { useCategoriesByActivity } from "@/hooks/useCategoriesByActivity";
import { useCategoryById } from "@/hooks/useCategories";
import { useHierarchicalCategory } from "@/hooks/useHierarchicalCategory";
import { useTopicsLegacy as useTopics } from "@/hooks/useTopicsLegacy";
import { useAuth } from "@/hooks/useAuth";
import { useCategoryStats } from "@/hooks/useCategoryStats";
import { formatDistanceToNow } from "date-fns";
import { QuickTopicModal } from "./QuickTopicModal";
import { CategoryRequestModal } from "./CategoryRequestModal";
import { CreateSubcategoryModal } from "./CreateSubcategoryModal";
import { AdminControls } from "./AdminControls";
import { usePermissions } from "@/hooks/usePermissions";
import { TopicTable } from "./TopicTable";
import { StateForumSeoContent } from "./StateForumSeoContent";
import { PaginationControls } from "@/components/ui/pagination-controls";
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

// Helper function to build breadcrumb hierarchy
const buildBreadcrumbHierarchy = (category: any) => {
  console.log("Building breadcrumb hierarchy for category:", category);
  const breadcrumbs = [];
  let current = category;

  // Build breadcrumb hierarchy by traversing parent_category chain
  while (current) {
    console.log(
      "Processing breadcrumb level:",
      current.name,
      "with parent:",
      current.parent_category
    );
    breadcrumbs.unshift({
      name: current.name,
      slug: current.slug,
      id: current.id,
    });

    // Move to parent category
    current = current.parent_category;
  }

  console.log("Final breadcrumbs array:", breadcrumbs);
  return breadcrumbs;
};

const SubcategoryCard = ({
  subcat,
  parentPath,
}: {
  subcat: any;
  parentPath: string[];
}) => {
  const { data: stats } = useCategoryStats(subcat.id);
  const to = "/" + [...parentPath, subcat.slug].join("/");

  return (
    <Link to={to}>
      <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: subcat.color }}
            />
            <h3 className="font-semibold text-sm text-gray-900">
              {subcat.name}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <ChevronRight className="h-4 w-4 text-gray-400" />
          </div>
        </div>
        <p className="text-xs text-gray-600 mb-3 line-clamp-2">
          {subcat.description}
        </p>
        <div className="flex items-center text-xs text-gray-500 space-x-4">
          <div className="flex items-center space-x-1">
            <MessageSquare className="h-3 w-3" />
            <span>{stats?.topic_count || 0} topics</span>
          </div>
          <div className="flex items-center space-x-1">
            <User className="h-3 w-3" />
            <span>{stats?.post_count || 0} posts</span>
          </div>
          {subcat.last_activity_at && (
            <div className="flex items-center space-x-1">
              <Clock className="h-3 w-3" />
              <span>
                {formatDistanceToNow(new Date(subcat.last_activity_at))} ago
              </span>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
};

export const CategoryView = () => {
  const params = useParams();
  const { user } = useAuth();
  const { canManageCategories } = usePermissions();

  console.log("CategoryView params:", params);

  // Parse URL segments for hierarchical structure
  const urlSegments = [
    params.level1Slug,
    params.level2Slug,
    params.level3Slug,
  ].filter(Boolean) as string[];

  // Check for legacy UUID routing
  const categoryId = params.categoryId;
  const isLegacyRoute = !!categoryId;
  const isUUID =
    categoryId &&
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      categoryId
    );

  console.log("CategoryView logic:", { 
    urlSegments, 
    isLegacyRoute, 
    isUUID,
    categoryId 
  });

  // Use hierarchical hook for new URL structure
  const {
    data: hierarchicalCategory,
    isLoading: hierarchicalLoading,
    error: hierarchicalError,
  } = useHierarchicalCategory(urlSegments);

  // Use legacy hook for UUID-based routing
  const {
    data: categoryById,
    isLoading: categoryByIdLoading,
    error: idError,
  } = useCategoryById(isUUID && categoryId ? categoryId : "");

  // Select the appropriate result
  let category, categoryLoading, categoryError;

  if (isLegacyRoute && isUUID) {
    console.log("Using legacy UUID categoryById result");
    category = categoryById;
    categoryLoading = categoryByIdLoading;
    categoryError = idError;
  } else {
    console.log("Using hierarchical category result");
    category = hierarchicalCategory;
    categoryLoading = hierarchicalLoading;
    categoryError = hierarchicalError;
  }

  console.log("Final category data:", category);

  const [page, setPage] = useState(1);
  const pageSize = 10;
  const { data: subcategories, isLoading: subcategoriesLoading } =
    useCategoriesByActivity(
      category?.id,
      category?.level ? category.level + 1 : undefined
    );
  const { data: topicsData, isLoading: topicsLoading } = useTopics(
    category?.id,
    page,
    pageSize
  );

  // Defensive single-slug redirect: when a level-1 lookup misses but the slug
  // exists at level 2 or 3, send the user to the canonical hierarchical URL
  // instead of showing "Category not found". Hook must be called before any
  // early returns to keep hook order stable across renders.
  const singleSlug =
    urlSegments.length === 1 && !isLegacyRoute ? urlSegments[0] : undefined;
  const shouldLookupCanonical = !categoryLoading && !category && !!singleSlug;
  const { data: canonicalPath, isLoading: canonicalLoading } =
    useCanonicalCategoryPath(shouldLookupCanonical ? singleSlug : undefined);

  if (categoryLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 bg-gray-200 rounded animate-pulse"></div>
        <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-20 bg-gray-200 rounded animate-pulse"
            ></div>
          ))}
        </div>
      </div>
    );
  }


  if (!category && !categoryLoading) {
    // 3-segment URL whose 3rd segment is NOT a level-3 category is almost
    // certainly a topic under the level-2 category (route-ranking ambiguity
    // between :l1/:l2/:l3 and :l1/:l2/:topicSlug). Render TopicView instead
    // of showing "not found".
    if (urlSegments.length === 3 && !isLegacyRoute) {
      return <TopicView />;
    }
    // 2-segment URL whose 2nd segment is NOT a level-2 category may be a
    // topic under a level-1 category — same fall-through.
    if (urlSegments.length === 2 && !isLegacyRoute) {
      return <TopicView />;
    }
    if (singleSlug && canonicalLoading) {
      return (
        <div className="space-y-6">
          <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
        </div>
      );
    }
    if (canonicalPath && canonicalPath !== `/${singleSlug}`) {
      return <Navigate to={canonicalPath} replace />;
    }
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900">
          Category not found
        </h2>
        <p className="text-gray-600 mt-2">
          The category you're looking for doesn't exist.
        </p>
        <Button asChild className="mt-4">
          <Link to="/">Back to Home</Link>
        </Button>
      </div>
    );
  }

  // Determine if we should show subcategories or topics
  const hasSubcategories = subcategories && subcategories.length > 0;
  const isLevel3Category = category?.level === 3; // Only Level 3 categories can have topics
  const topics = topicsData?.topics || [];
  const totalPages = Math.ceil((topicsData?.total || 0) / 20);
  const totalCount = topicsData?.total || 0;

  // Full hierarchical slug chain for the current category (root → leaf).
  // Used to build canonical URLs for subcategory cards & breadcrumbs.
  const categoryPath = category ? getCategoryPath(category) : [];


  return (
    <div className="forum-spacing w-full overflow-x-hidden">
      {/* Breadcrumb */}
      {category && (
        <Breadcrumb className="overflow-x-auto">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">
                  <Home className="h-4 w-4" />
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            {(() => {
              const crumbs = buildBreadcrumbHierarchy(category);
              const cumulative: string[] = [];
              return crumbs
                .map((breadcrumb, index, array) => {
                  cumulative.push(breadcrumb.slug);
                  const href = "/" + cumulative.join("/");
                  return [
                    <BreadcrumbSeparator key={`sep-${breadcrumb.id}`} />,
                    <BreadcrumbItem key={breadcrumb.id}>
                      {index === array.length - 1 ? (
                        <BreadcrumbPage className="max-w-full truncate">
                          {formatCategoryDisplayName(breadcrumb.name)}
                        </BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={href} className="max-w-full truncate">
                            {formatCategoryDisplayName(breadcrumb.name)}
                          </Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>,
                  ];
                })
                .flat();
            })()}
          </BreadcrumbList>
        </Breadcrumb>
      )}

      {/* Category Header */}
      {category && (
        <Card className="p-3 sm:p-4 w-full border border-forum-border-subtle">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: category.color }}
                  />
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900 min-w-0 truncate">
                    {formatCategoryDisplayName(category.name)}
                  </h1>
                </div>
                <AdminControls
                  content={category}
                  contentType="category"
                  onDelete={() => (window.location.href = "/")}
                />
              </div>
              <p className="text-gray-600 mb-4 text-sm sm:text-base">
                {category.description}
              </p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-gray-500">
                {category.region && <span>Region: {category.region}</span>}
                {category.birth_year && (
                  <span>Birth Year: {category.birth_year}</span>
                )}
                {category.play_level && (
                  <span>Level: {category.play_level}</span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              {/* Show different content based on category level and moderation requirements */}
              {category.level === 3 ||
              (category.level === 2 && !category.requires_moderation) ? (
                // Level 3 categories and level 2 categories without moderation allow topic creation
                <>
                  <QuickTopicModal
                    preselectedCategoryId={category.id}
                    trigger={
                      <Button size="sm" className="w-full sm:w-auto">
                        <Plus className="h-4 w-4 mr-2" />
                        Start Discussion
                      </Button>
                    }
                  />

                  {/* Create Subcategory button (admin only, Level 2 only) */}
                  {canManageCategories && category.level === 2 && (
                    <CreateSubcategoryModal
                      parentCategoryId={category.id}
                      parentCategoryName={category.name}
                      trigger={
                        <Button
                          variant="secondary"
                          size="sm"
                          className="w-full sm:w-auto"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create Subcategory
                        </Button>
                      }
                    />
                  )}

                  {/* Category request button */}
                  <CategoryRequestModal
                    currentCategoryId={category.id}
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                      >
                        <HelpCircle className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">
                          Request Category
                        </span>
                        <span className="sm:hidden">Request</span>
                      </Button>
                    }
                  />
                </>
              ) : (
                // Level 1 categories and level 2 categories requiring moderation are for browsing only
                <div className="flex flex-col items-center gap-2">
                  <Badge variant="secondary" className="text-xs">
                    Browse Only - Select a{" "}
                    {category.slug?.includes("general")
                      ? "Category"
                      : category.slug?.includes("tournaments")
                      ? "Location"
                      : category.slug?.includes("usa") ||
                        category.region === "USA"
                      ? "State"
                      : "Province"}{" "}
                    to Post
                  </Badge>
                  <CategoryRequestModal
                    currentCategoryId={category.id}
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                      >
                        <HelpCircle className="h-4 w-4 mr-2" />
                        <span className="hidden sm:inline">
                          Request Category
                        </span>
                        <span className="sm:hidden">Request</span>
                      </Button>
                    }
                  />
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* State-level SEO content (renders only on matching state forum slugs) */}
      {category && <StateForumSeoContent slug={category.slug} />}

      {/* Subcategories or Topics */}
      {category && hasSubcategories ? (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Browse Categories
            </h2>
            <div className="text-xs sm:text-sm text-gray-500">
              Can't find what you're looking for?{" "}
              <CategoryRequestModal
                currentCategoryId={category.id}
                trigger={
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 h-auto text-blue-600 text-xs sm:text-sm"
                  >
                    Request a new category
                  </Button>
                }
              />
            </div>
          </div>
          <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full">
            {subcategories.map((subcat) => (
              <SubcategoryCard
                key={subcat.id}
                subcat={subcat}
                parentPath={categoryPath}
              />
            ))}
          </div>
        </>
      ) : category ? (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
              Topics
            </h2>
          </div>
          <TopicTable
            topics={topics}
            categorySlug={category?.slug}
            loading={topicsLoading}
          />
          {(topicsLoading || topics.length > 0) && (
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              totalItems={totalCount}
              itemsPerPage={pageSize}
              onPageChange={setPage}
              loading={topicsLoading}
            />
          )}
        </>
      ) : null}
    </div>
  );
};
